// src/utils/employeeServices.js
// ============================================================
// Production-Ready Enterprise Role-Based Employee Services Engine
// Covers Employee -> Team Lead -> HR -> Admin -> HR -> Employee
// ============================================================

export const TICKET_CATEGORIES = [
  "Payroll",
  "Attendance",
  "Leave",
  "Shift",
  "Documents",
  "Benefits",
  "IT/System",
  "Other",
];

export const TICKET_STATUSES = [
  "Open",
  "Assigned",
  "Under Review",
  "In Progress",
  "Waiting for Employee",
  "Escalated to HR",
  "Waiting for Admin",
  "Resolved",
  "Closed",
  "Rejected",
];

export const TICKET_PRIORITIES = ["Low", "Medium", "High", "Urgent"];

export const ANNOUNCEMENT_AUDIENCES = [
  "Entire Organization",
  "Department",
  "Team",
  "Specific Employees",
];

export const RECOGNITION_CATEGORIES = [
  "Excellence",
  "Teamwork",
  "Innovation",
  "Customer Delight",
  "Leadership",
  "Appreciation",
];

export const EMPLOYEE_SERVICE_SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "helpdesk", label: "Help Desk" },
  { key: "announcements", label: "Announcements" },
  { key: "events", label: "Events" },
  { key: "recognition", label: "Recognition" },
  { key: "reports", label: "Reports & Analytics" },
];

export function getUserRoleLabel(role = "") {
  const normalized = (role || "").toLowerCase();
  if (normalized === "admin") return "Admin";
  if (normalized === "hr") return "HR";
  if (normalized === "lead") return "Team Lead";
  if (normalized === "employee") return "Employee";
  return "User";
}

export function canAccessSection(role, section) {
  const normalized = (role || "").toLowerCase();
  const allowedByRole = {
    admin: ["overview", "helpdesk", "announcements", "events", "recognition", "reports"],
    hr: ["overview", "helpdesk", "announcements", "events", "recognition", "reports"],
    lead: ["overview", "helpdesk", "announcements", "events", "recognition", "reports"],
    employee: ["overview", "helpdesk", "announcements", "events", "recognition", "reports"],
  };

  return (allowedByRole[normalized] || ["overview"]).includes(section);
}

export function canManageAnnouncements(role) {
  const normalized = (role || "").toLowerCase();
  return normalized === "admin" || normalized === "hr";
}

export function canManageEvents(role) {
  const normalized = (role || "").toLowerCase();
  return normalized === "admin" || normalized === "hr";
}

export function canManageServices(role) {
  const normalized = (role || "").toLowerCase();
  return normalized === "admin" || normalized === "hr";
}

export function canCreateRecognition(role) {
  // All roles can give recognition in the enterprise workflow
  return true;
}

export function canApproveRecognition(role) {
  const normalized = (role || "").toLowerCase();
  return normalized === "admin" || normalized === "hr" || normalized === "lead";
}

export function canEscalateToHR(role) {
  const normalized = (role || "").toLowerCase();
  return normalized === "lead" || normalized === "admin";
}

export function canEscalateToAdmin(role) {
  const normalized = (role || "").toLowerCase();
  return normalized === "hr";
}

export function canReturnToHR(role) {
  const normalized = (role || "").toLowerCase();
  return normalized === "admin";
}

export function canResolveTicket(role, ticket) {
  const normalized = (role || "").toLowerCase();
  if (normalized === "admin") return true;
  if (normalized === "hr") return true;
  if (normalized === "lead") {
    // Lead can resolve if not escalated beyond lead level
    return ticket?.escalationLevel !== "admin" && ticket?.escalationLevel !== "hr";
  }
  return false;
}

export function getStatusBadgeStyle(status = "") {
  const s = (status || "").toLowerCase();
  if (s === "resolved" || s === "closed" || s === "approved") {
    return {
      background: "#f0fdf4",
      color: "#15803d",
      border: "1.5px solid #bbf7d0",
    };
  }
  if (s === "escalated to hr" || s === "waiting for admin") {
    return {
      background: "#fffbeb",
      color: "#b45309",
      border: "1.5px solid #fde68a",
    };
  }
  if (s === "rejected") {
    return {
      background: "#fef2f2",
      color: "#b91c1c",
      border: "1.5px solid #fca5a5",
    };
  }
  if (s === "in progress" || s === "under review" || s === "assigned") {
    return {
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "1.5px solid #bfdbfe",
    };
  }
  if (s === "waiting for employee") {
    return {
      background: "#faf5ff",
      color: "#7e22ce",
      border: "1.5px solid #e9d5ff",
    };
  }
  // Default: Open
  return {
    background: "#f0fdf4",
    color: "#166534",
    border: "1.5px solid #86efac",
  };
}

export function getPriorityBadgeStyle(priority = "") {
  const p = (priority || "").toLowerCase();
  if (p === "urgent") {
    return {
      background: "#fef2f2",
      color: "#dc2626",
      border: "1px solid #fecaca",
      fontWeight: 700,
    };
  }
  if (p === "high") {
    return {
      background: "#fff7ed",
      color: "#ea580c",
      border: "1px solid #fed7aa",
      fontWeight: 700,
    };
  }
  if (p === "medium") {
    return {
      background: "#fefce8",
      color: "#ca8a04",
      border: "1px solid #fef08a",
      fontWeight: 600,
    };
  }
  return {
    background: "#f0fdf4",
    color: "#16a34a",
    border: "1px solid #bbf7d0",
    fontWeight: 600,
  };
}
